import type {ComponentType} from 'react';
import type {LoaderFunctionArgs, RouteObject} from 'react-router';
import {matchPath} from 'react-router';

import {handleChunkLoadError} from '#utils/chunk_load_error.js';

export type Route = {
  path: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  import: () => Promise<any>;
  options?: Omit<RouteObject, 'path' | 'element' | 'lazy' | 'children'>;
  children?: Route[];
};

export type RouteLoaderArgs<
  Props extends Record<string, unknown> = Record<string, unknown>,
> = LoaderFunctionArgs & {
  initialProps: Props;
};

function normalizePattern(input: string): string {
  // Collapse duplicate slashes
  return input.replace(/\/+/g, '/');
}

function routeMatchesPath(
  routePattern: string,
  concretePath: string,
  opts?: {includeDescendants?: boolean}
): boolean {
  try {
    return !!matchPath(
      {
        path: normalizePattern(routePattern),
        // end=false allows descendants to match (prefix match)
        end: !opts?.includeDescendants,
      },
      normalizePattern(concretePath)
    );
  } catch {
    return false;
  }
}

export function generateRoutes(
  routes: Route[],
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  initialProps?: Record<string, any>,
  basePath = ''
): RouteObject[] {
  return routes.map(route => {
    const path = route.path;
    const fullPattern = [basePath, path].filter(Boolean).join('/');
    const normalizedPattern = normalizePattern(fullPattern);
    // Resolve initial props by matching any provided concrete URL keys
    // against this route's pattern. For parent routes, allow descendant
    // paths to match so props from deeper URLs flow to the parent loader.
    let props = (initialProps && initialProps[normalizedPattern]) || {};

    if (
      !props ||
      (typeof props === 'object' && Object.keys(props).length === 0)
    ) {
      if (initialProps) {
        const keys = Object.keys(initialProps);
        const includeDescendants =
          Array.isArray(route.children) && route.children.length > 0;
        const matchingKeys = keys.filter(key =>
          routeMatchesPath(normalizedPattern, key, {includeDescendants})
        );
        // Prefer the most specific (longest) matching key
        const bestKey = matchingKeys.sort((a, b) => b.length - a.length)[0];
        if (bestKey) props = initialProps[bestKey];
      }
    }

    const routeObject: RouteObject = {
      path,
      lazy: async () => {
        let module;

        try {
          module = await route.import();
        } catch (error) {
          await handleChunkLoadError(error, 'react-router-lazy');
          throw error;
        }

        const result: {
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          Component: ComponentType<any>;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          loader?: (args: LoaderFunctionArgs) => any;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          action?: (args: LoaderFunctionArgs) => any;
          // eslint-disable-next-line @typescript-eslint/no-explicit-any
          ErrorBoundary?: ComponentType<any>;
        } = {
          Component: module.default,
        };

        if (module.loader) {
          result.loader = async (args: LoaderFunctionArgs) => {
            return await module.loader({
              ...args,
              initialProps: props,
            });
          };
        }

        if (module.action) result.action = module.action;
        if (module.ErrorBoundary) result.ErrorBoundary = module.ErrorBoundary;

        return result;
      },
      ...route.options,
    };

    // Add children if they exist
    if (route.children) {
      routeObject.children = generateRoutes(
        route.children,
        initialProps,
        normalizedPattern
      );
    }

    return routeObject;
  });
}
